import { Option, Predicate, Schema } from 'effect';

import {
  FacetKey,
  facetKeyEquivalence,
  type FacetKey as FacetKeyType,
} from '../../capability/productListing/facetKey';
import { ListingSort } from '../../capability/productListing/listingSelection';
import { FilterCode, FilterValueToken, SortToken } from '../url/filterTokens';

export const VocabularyValue = Schema.Struct({
  value: Schema.String,
  token: FilterValueToken,
});

export type VocabularyValue = typeof VocabularyValue.Type;

export const VocabularyEntry = Schema.TaggedUnion({
  Terms: {
    key: FacetKey,
    code: FilterCode,
    values: Schema.Array(VocabularyValue),
    indexable: Schema.Boolean,
  },
  Range: {
    key: FacetKey,
    code: FilterCode,
    scale: Schema.Finite,
    rounding: Schema.optionalKey(Schema.Literals(['exact', 'outward'])),
    defaultBounds: Schema.optionalKey(Schema.Struct({ min: Schema.Finite, max: Schema.Finite })),
  },
});

export type VocabularyEntry = typeof VocabularyEntry.Type;

export const ListingVocabulary = Schema.Struct({
  entries: Schema.Array(VocabularyEntry),
  sorts: Schema.Array(Schema.Struct({ token: SortToken, sort: ListingSort })),
});

export type ListingVocabulary = typeof ListingVocabulary.Type;

/** Collects range codes for the path syntax disambiguation hint. */
export const rangeCodesOf = (vocabulary: ListingVocabulary): ReadonlySet<string> => {
  const codes = new Set<string>();
  for (const entry of vocabulary.entries) {
    if (Predicate.isTagged('Range')(entry)) codes.add(entry.code);
  }
  return codes;
};

/** Finds the vocabulary entry for a facet key. */
export const entryForKey = (
  vocabulary: ListingVocabulary,
  key: FacetKeyType
): VocabularyEntry | undefined =>
  vocabulary.entries.find((entry) => facetKeyEquivalence(entry.key, key));

/** Finds the vocabulary entry for a URL filter code. */
export const entryForCode = (
  vocabulary: ListingVocabulary,
  code: string
): VocabularyEntry | undefined => vocabulary.entries.find((entry) => entry.code === code);

/** A value that an open terms entry passes to the URL unchanged. */
const isFilterValueToken = Schema.is(FilterValueToken);

/**
 * The URL token one value of a terms entry carries, or nothing where the
 * vocabulary cannot represent it.
 *
 * An entry that lists its values translates through that list. An entry that
 * lists none is open: the backend's own value goes into the URL where the token
 * alphabet accepts it, and a value it does not accept has no URL form at all.
 */
export const tokenForEntry = (
  entry: Extract<VocabularyEntry, { readonly _tag: 'Terms' }>,
  value: string
): FilterValueToken | undefined => {
  if (entry.values.length === 0) return isFilterValueToken(value) ? value : undefined;

  return entry.values.find((candidate) => candidate.value === value)?.token;
};

/**
 * The URL token one facet value carries.
 *
 * The same translation a whole selection goes through, for a caller that holds
 * one value at a time — a control offering a facet's options has to know, per
 * option, what it would put in the URL. Reading it from here is what keeps such
 * a control and the listing's own writer on one answer.
 */
export const tokenFor = (
  vocabulary: ListingVocabulary,
  key: FacetKeyType,
  value: string
): Option.Option<FilterValueToken> => {
  const entry = entryForKey(vocabulary, key);

  if (entry === undefined || !Predicate.isTagged('Terms')(entry)) return Option.none();

  return Option.fromNullishOr(tokenForEntry(entry, value));
};
